import { useContext, useMemo, useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Controller, FormProvider } from 'react-hook-form';
import { NumberInput, Select, TextInput } from 'react-hook-form-mantine';
import { CreatableSelect } from '@dealstack/frontend/shared/components/forms/CreatableSelect/CreatableSelect';
import {
	EDIT_SECURITY_DETAILS_FORM_FIELD,
	useEditSecurityDetailsController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/useEditSecurityDetailsController.ts';
import {
	formatNominalValue,
	makeSecurityTypeName,
	SECURITY_TYPE_OPTIONS,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';
import {
	formatNominalValueAsOfDate,
	getCurrentNominalValue,
	hasSplitAdjustedNominalValue,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getCurrentNominalValue.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { CURRENCIES_GROUPED_OPTIONS } from '@dealstack/frontend/shared/currencies.ts';
import { groupedSelectDividerStyles } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { FormSegmentedControl } from '@dealstack/frontend/shared/components/FormSegmentedControl/FormSegmentedControl';
import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string.ts';
import { SECURITY_NOMINAL_VALUE_PRECISION } from '@dealstack/frontend/shared/cap.ts';
import { SecurityFeatures } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/SecurityFeatures.tsx';
import { SecurityDetailsControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/useSecurityDetailsController.ts';
import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import {
	makeSecurityDenominatorLabel,
	makeSecurityUnitDenominatorOptions,
	SECURITY_EQUITY_BASIS_OPTIONS,
	SECURITY_ISSUED_IN_OPTIONS,
} from '@dealstack/frontend/shared/securities.ts';
import { NumberSelect } from '@dealstack/frontend/shared/components/forms/NumberSelect/NumberSelect.tsx';
import { UnitDenominatorSelectItem } from './UnitDenominatorSelectItem.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { Divider, Flex, Text, Tooltip } from '@mantine/core';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { DataLoanDefinitionTct } from '@dealstack/shared/lib/generated';
import { CreateLoanDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityTabs/SecurityOverviewTabPanel/CreateLoanDrawer/CreateLoanDrawer.tsx';
import { SecurityWarningModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityWarningModal.tsx';
import {
	makeBooleanDropdownOptions,
	securityFeatureBlockTranslation,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { evaluateFieldLock } from '@dealstack/shared/lib/common/securities/securityFeatureRules';
import { makeSecurityFeatureSnapshot } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatureSnapshot.ts';

interface SecurityOverviewTabPanelProps {
	hasEditorPermission?: boolean;
}

export const SecurityOverviewTabPanel = ({
	hasEditorPermission,
}: SecurityOverviewTabPanelProps) => {
	const tCapManage = useTranslationCapManage();
	const tCommonUi = useTranslationCommonUi();

	const {
		getSecurityDetailsRequest,
		refetchSecurityDetails,
		loanDefinitionsOptions,
		refetchLoanDefinitions,
		filterDate,
	} = useContext(SecurityDetailsControllerContext);

	const data = getSecurityDetailsRequest?.data;

	const {
		editSecurityForm,
		onSubmitDetails,
		setEditMode,
		editMode,
		isIssuedInUnits,
		allowFractionalOwnership,
		updateSecurityRequest,
		handleResidueRightsChange,
		disableIsMgtLoanToggle,
		disableLoanDefinitionSelect,
		handleIssuedInChange,
		warningModalData,
	} = useEditSecurityDetailsController(data, refetchSecurityDetails);

	const existingUnitDenominator = data?.unitDenominator;
	const disableUnitDenominatorToggle = !isIssuedInUnits || existingUnitDenominator !== 1;

	const booleanOptions = useMemo(() => makeBooleanDropdownOptions(tCommonUi), [tCommonUi]);
	const nominalValueField = useMemo(
		() => formatNominalValue(data?.nominalValue),
		[data?.nominalValue]
	);
	const currentNominalValueField = useMemo(
		() => (data ? formatNominalValue(getCurrentNominalValue(data)) : LONG_DASH),
		[data]
	);
	const showSplitAdjustedNominalValue = !!data && hasSplitAdjustedNominalValue(data);

	const handleCancel = () => {
		setEditMode(false);
	};

	const [showCreateNewLoanDefinitionDrawer, setShowCreateNewLoanDefinitionDrawer] =
		useState(false);

	const onCreateLoanDefinitionSuccess = async (createdLoanDefinition: DataLoanDefinitionTct) => {
		await refetchLoanDefinitions();

		editSecurityForm.setValue(
			EDIT_SECURITY_DETAILS_FORM_FIELD.loanDefinitionId,
			createdLoanDefinition.id,
			{ shouldValidate: true }
		);

		setShowCreateNewLoanDefinitionDrawer(false);
	};

	const selectedLoanDefintionName = useMemo(() => {
		return loanDefinitionsOptions?.find((ld) => ld.value === data?.loanDefinitionId)?.label;
	}, [loanDefinitionsOptions, data?.loanDefinitionId]);

	const exchangedForTooltipLabel = useMemo(
		() =>
			tCapManage('securities.details.issued_in.exchanged_for.restrictions', {
				securityNames: data?.exchangedFrom?.map((sec) => sec?.security?.name)?.join(', '),
			}),
		[data?.exchangedFrom, tCapManage]
	);

	// Overlay unsaved edit-form values on top of the persisted snapshot so field locks track
	// residueRights/issuedIn changes live, matching the confirmed-cascade preview.
	const formIssuedIn = editSecurityForm.watch(EDIT_SECURITY_DETAILS_FORM_FIELD.issuedIn);
	const formResidueRights = editSecurityForm.watch(
		EDIT_SECURITY_DETAILS_FORM_FIELD.residueRights
	);
	const snapshot = data
		? {
				...makeSecurityFeatureSnapshot(data),
				...(editMode && formIssuedIn ? { issuedIn: formIssuedIn } : {}),
				...(editMode && typeof formResidueRights === 'boolean'
					? { residueRights: formResidueRights }
					: {}),
			}
		: undefined;
	const fieldLock = (field: Parameters<typeof evaluateFieldLock>[0]) =>
		snapshot ? evaluateFieldLock(field, snapshot) : { locked: false };
	const editLockedLabel = (labelText: string, field: Parameters<typeof evaluateFieldLock>[0]) => {
		const lock = fieldLock(field);
		if (!editMode || !lock.reason) {
			return labelText;
		}
		const translation = securityFeatureBlockTranslation(lock.reason);

		return (
			<LabelWithTooltip
				labelText={labelText}
				tooltipLabel={tCapManage(translation.key, {
					defaultValue: translation.defaultValue,
				})}
			/>
		);
	};
	const issuedInLabelText = tCapManage('securities.drawer.issuedIn.label');
	const issuedInLock = fieldLock('issuedIn');
	const issuedInLockTranslation = issuedInLock.reason
		? securityFeatureBlockTranslation(issuedInLock.reason)
		: undefined;
	const issuedInLabel = issuedInLock.locked ? (
		<LabelWithTooltip
			labelText={issuedInLabelText}
			tooltipLabel={
				issuedInLockTranslation
					? tCapManage(issuedInLockTranslation.key, {
							defaultValue: issuedInLockTranslation.defaultValue,
						})
					: exchangedForTooltipLabel
			}
		/>
	) : (
		issuedInLabelText
	);

	const residueRightsLock = fieldLock('residueRights');
	const disableResidueRights = residueRightsLock.locked;

	const residueRightsLabel = useMemo(() => {
		const residueRightsLabelText = tCapManage('securities.drawer.residueRights.label');

		if (!residueRightsLock.locked || !residueRightsLock.reason) {
			return residueRightsLabelText;
		}
		const translation = securityFeatureBlockTranslation(residueRightsLock.reason);

		return (
			<LabelWithTooltip
				labelText={residueRightsLabelText}
				tooltipLabel={tCapManage(translation.key, {
					defaultValue: translation.defaultValue,
				})}
			/>
		);
	}, [residueRightsLock, tCapManage]);

	const includeInReturnsLabel = useMemo(
		() => (
			<LabelWithTooltip
				labelText={tCapManage('securities.drawer.includeInReturns.label', {
					defaultValue: 'Include in returns',
				})}
				tooltipLabel={tCapManage('securities.drawer.includeInReturns.tooltip', {
					defaultValue:
						'When off, this security is left out of MOIC and XIRR - both its value and the money paid for it. Proceeds and sources & uses are unaffected.',
				})}
			/>
		),
		[tCapManage]
	);

	return (
		<Flex direction="column" w="100%">
			<EditableInfoContainer
				title={tCapManage('securities.details.edit_button')}
				isEdit={editMode}
				setIsEdit={setEditMode}
				disableButtons={updateSecurityRequest.isPending}
				onUpdate={editSecurityForm.handleSubmit(onSubmitDetails)}
				onCancel={handleCancel}
				hideRemove={true}
				hasEditorPermission={hasEditorPermission}
				editBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_EDIT_BUTTON}
				updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_EDIT_SAVE_BUTTON}
				skipTopBorder={true}
			>
				<FormProvider {...editSecurityForm}>
					<form
						onSubmit={editSecurityForm.handleSubmit(onSubmitDetails)}
						style={{ width: '100%' }}
					>
						<EditableInfoContainerGrid $gridColumns={4}>
							<DetailEditableField
								editMode={editMode}
								label={tCapManage('securities.drawer.name.label')}
								data={data?.name}
								formInput={
									<TextInput
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.name}
										control={editSecurityForm.control}
										size="md"
									/>
								}
							/>

							<DetailEditableField
								editMode={editMode}
								label={tCapManage('securities.drawer.type.label')}
								data={data?.type ? makeSecurityTypeName(data.type) : LONG_DASH}
								formInput={
									<Select
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.type}
										control={editSecurityForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={SECURITY_TYPE_OPTIONS}
									/>
								}
							/>

							<DetailEditableField
								editMode={editMode}
								label={tCapManage('securities.drawer.currency.label')}
								data={data?.currency}
								formInput={
									<Select
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.currency}
										control={editSecurityForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={CURRENCIES_GROUPED_OPTIONS}
										disabled
										styles={groupedSelectDividerStyles}
									/>
								}
							/>

							<DetailEditableField
								editMode={editMode}
								label={issuedInLabel}
								data={normalizeScreamingSnakeToUI(data?.issuedIn ?? '')}
								formInput={
									<FormSegmentedControl
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.issuedIn}
										data={SECURITY_ISSUED_IN_OPTIONS}
										mt="0.25rem"
										onChange={handleIssuedInChange}
										disabled={issuedInLock.locked}
									/>
								}
							/>

							{showSplitAdjustedNominalValue && (
								<DetailEditableField
									editMode={false}
									label={tCapManage('securities.nominal_value_per_unit.as_of', {
										date: formatNominalValueAsOfDate(filterDate),
									})}
									data={currentNominalValueField}
									formInput={undefined}
								/>
							)}

							<DetailEditableField
								editMode={editMode}
								label={editLockedLabel(
									showSplitAdjustedNominalValue
										? tCapManage('securities.nominal_value_per_unit.original')
										: tCapManage('securities.drawer.nominal_value.label'),
									'nominalValue'
								)}
								data={nominalValueField}
								formInput={
									<NumberInput
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.nominalValue}
										control={editSecurityForm.control}
										size="md"
										disabled={fieldLock('nominalValue').locked}
										hideControls={true}
										decimalScale={SECURITY_NOMINAL_VALUE_PRECISION}
									/>
								}
							/>

							<DetailEditableField
								editMode={editMode}
								label={editLockedLabel(
									tCapManage('securities.drawer.votes_per_unit.label'),
									'votesPerUnit'
								)}
								data={data?.votesPerUnit ?? LONG_DASH}
								formInput={
									<NumberInput
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.votesPerUnit}
										control={editSecurityForm.control}
										disabled={fieldLock('votesPerUnit').locked}
										hideControls={true}
										size="md"
									/>
								}
							/>

							<DetailEditableField
								editMode={editMode}
								label={residueRightsLabel}
								data={
									data?.residueRights
										? tCommonUi('boolean.true')
										: tCommonUi('boolean.false')
								}
								formInput={
									<FormSegmentedControl
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.residueRights}
										handleAsBoolean
										data={booleanOptions}
										data-testid={
											TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_EDIT_RESIDUE_RIGHTS_TOGGLE
										}
										mt="0.25rem"
										onChange={handleResidueRightsChange}
										disabled={disableResidueRights}
									/>
								}
							/>

							<DetailEditableField
								editMode={editMode}
								label={includeInReturnsLabel}
								data={
									data?.includeInReturns === false
										? tCommonUi('boolean.false')
										: tCommonUi('boolean.true')
								}
								formInput={
									<FormSegmentedControl
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.includeInReturns}
										handleAsBoolean
										data={booleanOptions}
										data-testid={
											TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_EDIT_INCLUDE_IN_RETURNS_TOGGLE
										}
										mt="0.25rem"
									/>
								}
							/>

							<DetailEditableField
								editMode={editMode}
								label={editLockedLabel(
									tCapManage('securities.drawer.equityBasis.label'),
									'equityBasis'
								)}
								data={normalizeScreamingSnakeToUI(data?.equityBasis ?? LONG_DASH)}
								formInput={
									<Select
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.equityBasis}
										control={editSecurityForm.control}
										placeholder={tCapManage(
											'securities.drawer.equityBasis.placeholder'
										)}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={SECURITY_EQUITY_BASIS_OPTIONS}
										disabled={fieldLock('equityBasis').locked}
									/>
								}
							/>
							<DetailEditableField
								editMode={editMode}
								label={tCapManage('securities.drawer.fractionalOwnership.label')}
								data={makeSecurityDenominatorLabel(
									tCommonUi,
									data?.unitDenominator
								)}
								valueDataTestId={
									TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FRACTIONAL_DENOMINATOR
								}
								formInput={
									<FormSegmentedControl
										name={
											EDIT_SECURITY_DETAILS_FORM_FIELD.allowFractionalOwnership
										}
										handleAsBoolean
										data={booleanOptions}
										disabled={disableUnitDenominatorToggle}
										mt="0.25rem"
										data-testid={
											TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_EDIT_FRACTIONAL_TOGGLE
										}
									/>
								}
							/>
							<DetailEditableField
								editMode={editMode}
								label=""
								data=""
								formInput={
									<NumberSelect
										name={EDIT_SECURITY_DETAILS_FORM_FIELD.unitDenominator}
										placeholder={tCommonUi('common_placeholders.select')}
										disabled={!allowFractionalOwnership || !isIssuedInUnits}
										selectItemComponent={UnitDenominatorSelectItem}
										data={makeSecurityUnitDenominatorOptions(
											tCommonUi,
											existingUnitDenominator
										)}
										data-testid={
											TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_EDIT_FRACTIONAL_DROPDOWN
										}
									/>
								}
							/>
						</EditableInfoContainerGrid>

						{!disableIsMgtLoanToggle && (
							<EditableInfoContainerGrid $gridColumns={2} $alignItems="baseline">
								<DetailEditableField
									editMode={editMode}
									label={tCapManage('securities.drawer.is_mgt_loan.label')}
									data={
										data?.loanDefinitionId
											? tCommonUi('boolean.true')
											: tCommonUi('boolean.false')
									}
									style={{ maxWidth: '12rem' }}
									formInput={
										<FormSegmentedControl
											name={EDIT_SECURITY_DETAILS_FORM_FIELD.isMgtLoan}
											handleAsBoolean
											data={booleanOptions}
											disabled={disableIsMgtLoanToggle}
											mt="0.25rem"
										/>
									}
								/>

								<DetailEditableField
									editMode={editMode}
									label={tCapManage(
										'securities.drawer.mgt_loan_definition.label'
									)}
									data={selectedLoanDefintionName ?? LONG_DASH}
									style={{ minWidth: '25rem' }}
									formInput={
										<Controller
											name={EDIT_SECURITY_DETAILS_FORM_FIELD.loanDefinitionId}
											control={editSecurityForm.control}
											render={({ field }) => (
												<CreatableSelect
													value={field.value ?? null}
													onChange={field.onChange}
													placeholder={tCommonUi(
														'common_placeholders.select'
													)}
													size="md"
													data={loanDefinitionsOptions ?? []}
													disabled={disableLoanDefinitionSelect}
													getCreateLabel={() =>
														tCapManage(
															'securities.drawer.mgt_loan_definition.create_new'
														)
													}
													onCreate={() => {
														setShowCreateNewLoanDefinitionDrawer(true);
													}}
													shouldCreate={() => true}
												/>
											)}
										/>
									}
								/>
							</EditableInfoContainerGrid>
						)}
					</form>
				</FormProvider>
			</EditableInfoContainer>
			{/* Share Capital Totals Section */}
			<Divider c="gray.4" />
			<Flex w="100%">
				<Flex align="flex-start" w="100%">
					<Flex
						direction="column"
						gap="0.5rem"
						p="0.5rem"
						miw="264px"
						maw="264px"
						h="100%"
					>
						<Text fw={600} lineClamp={1} w="100%" truncate>
							{tCapManage('securities.details.share_capital_totals', {
								defaultValue: 'Share Capital Totals',
							})}
							{filterDate && (
								<Text component="span" size="xs" c="dimmed" ml="0.5rem">
									(
									{tCapManage('securities.details.as_of_date', {
										defaultValue: 'as of {{date}}',
										date: filterDate.toLocaleDateString(),
									})}
									)
								</Text>
							)}
						</Text>
					</Flex>
					<EditableInfoContainerGrid $gridColumns={4}>
						<DetailEditableField
							editMode={false}
							label={tCapManage('securities.details.total_share_capital', {
								defaultValue: 'Total Share Capital',
							})}
							data={
								data?.totalShareCapital !== undefined
									? formatNumber({
											value: data.totalShareCapital,
											lockPrecision: true,
											minPrecision: 2,
											maxPrecision: 2,
										})
									: LONG_DASH
							}
							formInput={undefined}
						/>
						<DetailEditableField
							editMode={false}
							label={tCapManage('securities.details.total_nominal_value', {
								defaultValue: 'Total Nominal Value',
							})}
							data={
								data?.totalNominalValue !== undefined
									? formatNumber({
											value: data.totalNominalValue,
											lockPrecision: true,
											minPrecision: 2,
											maxPrecision: 2,
										})
									: LONG_DASH
							}
							formInput={undefined}
						/>
						<DetailEditableField
							editMode={false}
							label={tCapManage('securities.details.total_share_premium', {
								defaultValue: 'Total Share Premium',
							})}
							data={
								data?.totalSharePremium !== undefined
									? formatNumber({
											value: data.totalSharePremium,
											lockPrecision: true,
											minPrecision: 2,
											maxPrecision: 2,
										})
									: LONG_DASH
							}
							formInput={undefined}
						/>
						<DetailEditableField
							editMode={false}
							label={tCapManage('securities.details.total_in_issue', {
								defaultValue: 'Total in Issue',
							})}
							data={
								data?.totalInIssue !== undefined
									? formatNumber({
											value: data.totalInIssue,
											lockPrecision: true,
											minPrecision: 0,
											maxPrecision: 6,
										})
									: LONG_DASH
							}
							formInput={undefined}
						/>
					</EditableInfoContainerGrid>
				</Flex>
			</Flex>
			<SecurityFeatures
				security={data ?? null}
				refreshDetails={refetchSecurityDetails}
				hasEditorPermission={hasEditorPermission}
			/>
			{warningModalData && <SecurityWarningModal {...warningModalData} />}
			<CreateLoanDrawer
				opened={showCreateNewLoanDefinitionDrawer}
				onClose={() => {
					setShowCreateNewLoanDefinitionDrawer(false);
				}}
				onSuccess={onCreateLoanDefinitionSuccess}
			/>
		</Flex>
	);
};

interface LabelWithTooltipProps {
	labelText: string;
	tooltipLabel: string;
}

const LabelWithTooltip = ({ labelText, tooltipLabel }: LabelWithTooltipProps) => (
	<Flex gap="0.5rem">
		{labelText}

		<Tooltip label={tooltipLabel}>
			<Flex c="dark.8" className="tooltip-icon">
				<Icon variant="information" size="lg" />
			</Flex>
		</Tooltip>
	</Flex>
);
